import { useI18n } from "@baton/i18n";
import type { ReactNode } from "react";
import { cn } from "../../../cn.ts";

/**
 * The shared frame of Input, Textarea, NumberInput and Select (§ Names, roles and structure):
 * a visible label rather than a placeholder label, required marked in text as well as with the
 * asterisk, the message wired through `aria-describedby`. The label is shadcn/ui's Label, the
 * one Spectrum UI's Floating Label Input is composed from (2026-09-15, by the owner's
 * decision); it stays above the field rather than floating inside it, because the floating
 * pattern needs an empty placeholder and has no room for the prefix slot, and both are in use.
 */
export interface FieldFrameProps {
  readonly id: string;
  readonly label: ReactNode;
  readonly hideLabel?: boolean;
  readonly required?: boolean;
  readonly message?: ReactNode;
  readonly invalid?: boolean;
  readonly messageId: string;
  readonly className?: string;
  readonly children: ReactNode;
  readonly after?: ReactNode;
}

/** The mark after a required field's label: the asterisk for the eye and the word for a screen reader, so a label drawn outside `FieldFrame`, a picker's, marks it the same way. */
export function RequiredMark() {
  const { t } = useI18n();
  return (
    <span data-slot="required-mark" className="font-normal text-muted-foreground">
      <span aria-hidden="true">*</span>
      <span className="sr-only">{t("ui.field.required")}</span>
    </span>
  );
}

export function FieldFrame({ id, label, hideLabel = false, required = false, message, invalid = false, messageId, className, children, after }: FieldFrameProps) {
  return (
    <div data-slot="field" className={cn("flex flex-col gap-2", className)}>
      <label htmlFor={id} data-slot="label" className={hideLabel ? "sr-only" : "flex items-center gap-1 text-sm leading-none font-medium select-none"}>
        {label}
        {required && " "}
        {required && <RequiredMark />}
      </label>
      {children}
      {after}
      {message !== undefined && message !== null && (
        <p id={messageId} data-slot="field-message" className={cn("text-xs text-muted-foreground", invalid && "text-accent-red-text")}>
          {message}
        </p>
      )}
    </div>
  );
}

export function describedBy(messageId: string, hasMessage: boolean, external?: string): string | undefined {
  const ids = [hasMessage ? messageId : null, external ?? null].filter((v): v is string => v !== null);
  return ids.length > 0 ? ids.join(" ") : undefined;
}
